import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useClientBotType } from '@/hooks/useClientBotType';
import { tracesApiService } from '@/services/api/traces.service';
import type { 
  TraceListParams, 
  TraceListResponse,
  TraceResponse,
  TraceAggregatedMetrics
} from '@/types/traces';

/**
 * Hook para obtener lista de traces con filtros y paginación - NUEVO CON TAGS
 */
export const useTraces = (params: Partial<TraceListParams> = {}) => {
  const { user, isAuthenticated } = useAuth();
  const { clientDetails, isLoading: clientLoading } = useClientBotType();

  return useQuery<TraceListResponse>({
    queryKey: ['traces', params, clientDetails?.vapi_tag],
    queryFn: async () => {
      if (!isAuthenticated) {
        throw new Error('User not authenticated');
      }

      // Para usuarios CLIENT: usar su vapi_tag específico  
      // Para ADMIN/DEVELOPER: pueden ver todos los traces o filtrar por tags múltiples
      const queryParams: TraceListParams = {
        ...params,
        ...(user?.role === 'client' && clientDetails?.vapi_tag && {
          tags: [clientDetails.vapi_tag] // Solo su tag específico
        })
      };

      console.log('useTraces: Fetching with params:', queryParams);

      return tracesApiService.listTraces(queryParams);
    },
    enabled: isAuthenticated && (user?.role !== 'client' || !!clientDetails?.vapi_tag),
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook para obtener un trace específico por ID
 */
export const useTrace = (traceId: string) => {
  const { user, isAuthenticated } = useAuth();
  const { clientDetails } = useClientBotType();

  return useQuery<TraceResponse>({
    queryKey: ['trace', traceId, clientDetails?.vapi_tag],
    queryFn: async () => {
      if (!isAuthenticated || !traceId) {
        throw new Error('User not authenticated or no trace ID available');
      }

      const tags = user?.role === 'client' && clientDetails?.vapi_tag 
        ? [clientDetails.vapi_tag] 
        : undefined;

      return tracesApiService.getTrace(traceId, tags);
    },
    enabled: isAuthenticated && !!traceId,
    staleTime: 10 * 60 * 1000, // 10 minutos
    retry: 2,
  });
};

/**
 * Hook para obtener métricas agregadas de traces
 */
export const useTraceMetrics = (params?: {
  fromTimestamp?: string;
  toTimestamp?: string;
  environment?: string[];
  tags?: string[];
}) => {
  const { user } = useAuth();
  const token = user?.id;

  return useQuery<TraceAggregatedMetrics>({
    queryKey: ['trace-metrics', user?.client_project_id, params],
    queryFn: async () => {
      if (!token || !user?.client_project_id) {
        throw new Error('No authentication token or client project ID available');
      }

      return tracesApiService.getTraceMetrics(token, user.client_project_id, params);
    },
    enabled: !!token && !!user?.client_project_id,
    staleTime: 2 * 60 * 1000, // 2 minutos para métricas
    retry: 2,
    refetchInterval: 5 * 60 * 1000, // Refrescar cada 5 minutos
  });
};

/**
 * Hook para obtener estadísticas del dashboard
 */
export const useTraceDashboardStats = () => {
  const { user } = useAuth();
  const token = user?.id;

  return useQuery({
    queryKey: ['trace-dashboard-stats', user?.client_project_id],
    queryFn: async () => {
      if (!token || !user?.client_project_id) {
        throw new Error('No authentication token or client project ID available');
      }

      return tracesApiService.getDashboardStats(token, user.client_project_id);
    },
    enabled: !!token && !!user?.client_project_id,
    staleTime: 1 * 60 * 1000, // 1 minuto
    retry: 2,
    refetchInterval: 2 * 60 * 1000, // Refrescar cada 2 minutos
  });
};

/**
 * Hook para buscar traces por texto
 */
export const useSearchTraces = (
  searchQuery: string, 
  params: Omit<TraceListParams, 'name'> = {}
) => {
  const { user, isAuthenticated } = useAuth();
  const { clientDetails } = useClientBotType();

  return useQuery<TraceListResponse>({
    queryKey: ['search-traces', searchQuery, params, clientDetails?.vapi_tag],
    queryFn: async () => {
      if (!isAuthenticated) {
        throw new Error('User not authenticated');
      }

      const searchParams = {
        ...params,
        ...(user?.role === 'client' && clientDetails?.vapi_tag && {
          tags: [clientDetails.vapi_tag]
        })
      };

      return tracesApiService.searchTraces(searchQuery, searchParams);
    },
    enabled: isAuthenticated && !!searchQuery && searchQuery.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 1,
  });
};

/**
 * Hook para obtener traces de una sesión específica
 */
export const useTracesBySession = (sessionId: string) => {
  const { user, isAuthenticated } = useAuth();
  const { clientDetails } = useClientBotType();

  return useQuery<TraceListResponse>({
    queryKey: ['traces-by-session', sessionId, clientDetails?.vapi_tag],
    queryFn: async () => {
      if (!isAuthenticated || !sessionId) {
        throw new Error('User not authenticated or no session ID available');
      }

      const tags = user?.role === 'client' && clientDetails?.vapi_tag 
        ? [clientDetails.vapi_tag] 
        : undefined;

      return tracesApiService.getTracesBySession(sessionId, tags);
    },
    enabled: isAuthenticated && !!sessionId,
    staleTime: 10 * 60 * 1000, // 10 minutos
    retry: 2,
  });
};

/**
 * Hook para obtener traces de un usuario específico
 */
export const useTracesByUser = (
  userId: string,
  dateRange?: { from: string; to: string }
) => {
  const { user, isAuthenticated } = useAuth();
  const { clientDetails } = useClientBotType();

  return useQuery<TraceListResponse>({
    queryKey: ['traces-by-user', userId, dateRange, clientDetails?.vapi_tag],
    queryFn: async () => {
      if (!isAuthenticated || !userId) {
        throw new Error('User not authenticated or no user ID available');
      }

      const tags = user?.role === 'client' && clientDetails?.vapi_tag 
        ? [clientDetails.vapi_tag] 
        : undefined;

      return tracesApiService.getTracesByUser(userId, tags, dateRange);
    },
    enabled: isAuthenticated && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 2,
  });
};

/**
 * Función utilitaria para invalidar queries de traces
 */
export const useInvalidateTraces = () => {
  const queryClient = useQueryClient();
  const { clientDetails } = useClientBotType();

  return {
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: ['traces'] });
      queryClient.invalidateQueries({ queryKey: ['trace-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['trace-dashboard-stats'] });
    },
    invalidateList: () => {
      queryClient.invalidateQueries({ queryKey: ['traces', clientDetails?.vapi_tag] });
    },
    invalidateMetrics: () => {
      queryClient.invalidateQueries({ queryKey: ['trace-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['trace-dashboard-stats'] });
    }
  };
};